import { BeaconMilestonesPanel } from "../components/BeaconMilestonesPanel";
import { CouncilThreadReply } from "../components/CouncilThreadReply";
import React, { useEffect, useState } from "react";
import { SignalPulse } from "../components/SignalPulse";
import { BeaconLaunchWindow } from "../components/BeaconLaunchWindow";
import { Check, X, Loader2, Plus, Copy, RefreshCw, Download, Flag, Trash2, Ban, Users, HeartHandshake, Bitcoin, Pencil, Send, Star, ChevronDown, Clock, Mail, Archive, Undo2, ExternalLink, ShieldCheck, MessageSquare } from "lucide-react";
import api from "../lib/api";
import { Avatar } from "../components/Media";
import { CategoryPill } from "./Feed";
import { useCrypto } from "../context/CryptoContext";
import { useAuth } from "../context/AuthContext";
import { CouncilManager } from "../components/CouncilManager";
import { TipsManager } from "../components/TipsManager";
import { StreamManager } from "../components/StreamManager";
import { FunnelSteps, FunnelSummary } from "../components/FunnelSteps";

export default function Admin() {
  const { issueEnvelopeFor, hasNetwork, unlockSafetyKey, decryptSafety, initSafetyKey, grantSafetyToStewards, requestBreakGlass, cosignBreakGlass, combineBreakGlass } = useCrypto();
  const { user } = useAuth();
  const isFounder = !!user?.is_founder;
  const perms = user?.council_perms || [];
  const can = (p) => isFounder || perms.includes(p);
  const [copied, setCopied] = useState(false);
  const SITE = "https://sovereignsignal.app";
  const copySite = async () => { try { await navigator.clipboard.writeText(SITE); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch {} };
  const tabVisible = (t) => {
    if (t === "watch_inbox") return can("watch") || can("disturbances");
    if (t === "council") return isFounder;
    if (t === "brand") return can("refresh");
    return can(t);
  };
  const tabList = ["applications", "requests", "watch_inbox", "beacon", "stream", "tips", "growth", "invites", "creed", "support", "quiet", "backup", "brand", "council"].filter(tabVisible);
  const [tab, setTab] = useState(tabList[0] || "applications");
  const [apps, setApps] = useState(null);
  const [invites, setInvites] = useState(null);
  const [showAuto, setShowAuto] = useState(false);
  const [memberInvites, setMemberInvites] = useState(null);
  const [profileReferralCount, setProfileReferralCount] = useState(null);
  const [inviteLabel, setInviteLabel] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [editingLabel, setEditingLabel] = useState(null);
  const [labelDraft, setLabelDraft] = useState("");
  const [copiedMsg, setCopiedMsg] = useState(null);
  const [stats, setStats] = useState(null);
  const [growth, setGrowth] = useState(null);
  const [summaryCopied, setSummaryCopied] = useState(false);
  const [announceState, setAnnounceState] = useState("");

  const postAnnouncement = async () => {
    setAnnounceState("posting");
    try {
      const { data } = await api.post("/admin/stream/announce");
      setAnnounceState(`Posted · ${data.new_members} new ${data.new_members === 1 ? "sovereign" : "sovereigns"}`);
      setTimeout(() => setAnnounceState(""), 4000);
    } catch { setAnnounceState("Could not post. Try again."); setTimeout(() => setAnnounceState(""), 4000); }
  };

  const buildGrowthSummary = () => {
    if (!growth) return "";
    const [y, m] = (growth.month || "").split("-");
    const monthLabel = y ? new Date(Number(y), Number(m) - 1, 1).toLocaleString(undefined, { month: "long", year: "numeric" }) : "this month";
    const lines = [`✦ State of the Signal: ${monthLabel}`, "",
      `New sovereigns this month: ${growth.total_month}`,
      `Signups (last 30 days): ${growth.total_30d}`];
    if (growth.top_of_month) lines.push(`Top Sovereign this month: ${growth.top_of_month.name || "A sovereign"} (${growth.top_of_month.count} ${growth.top_of_month.count === 1 ? "referral" : "referrals"})`);
    if (growth.top_referrers?.length) {
      lines.push("", "Top referrers:");
      growth.top_referrers.slice(0, 5).forEach((r, i) => lines.push(`${i + 1}. ${r.name || "A sovereign"}: ${r.count}`));
    }
    if (growth.by_source?.length) {
      lines.push("", "Signups by source:");
      growth.by_source.forEach((s) => lines.push(`• ${s.source}: ${s.count}`));
    }
    return lines.join("\n");
  };
  const downloadOverview = async (path = "/admin/overview-pdf", name = "Sovereign-Signal-Overview.pdf") => {
    try {
      const { data } = await api.get(path, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(data);
      a.download = name;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 2000);
    } catch { window.alert("Couldn't download the overview, please try again."); }
  };
  const copyGrowthSummary = async () => {
    try {
      await navigator.clipboard.writeText(buildGrowthSummary());
      setSummaryCopied(true); setTimeout(() => setSummaryCopied(false), 2000);
    } catch { /* ignore */ }
  };
  const [expandedKey, setExpandedKey] = useState(null);
  const [creating, setCreating] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [backupStats, setBackupStats] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [watch, setWatch] = useState(null);
  const [breakGlass, setBreakGlass] = useState([]);
  const [bgAudit, setBgAudit] = useState([]);
  const [watchSteward, setWatchSteward] = useState(false);
  const [safetyState, setSafetyState] = useState(null); // {exists, is_steward, provisioned}
  const [safetyBusy, setSafetyBusy] = useState(false);
  const [blockedReturns, setBlockedReturns] = useState(null);
  const [dormant, setDormant] = useState(null);
  const [dormantBusy, setDormantBusy] = useState(null);
  const [watchSection, setWatchSection] = useState("inbox");
  const [nudgeStatus, setNudgeStatus] = useState(null);
  const [quietFilter, setQuietFilter] = useState("all");
  const [beacons, setBeacons] = useState(null);
  const [cryptoCfg, setCryptoCfg] = useState(null);
  const [savingCrypto, setSavingCrypto] = useState(false);
  const [cryptoSaved, setCryptoSaved] = useState(false);
  const [donations, setDonations] = useState(null);
  const [requests, setRequests] = useState(null);
  const [resendingMissed, setResendingMissed] = useState(false);
  const [missedResult, setMissedResult] = useState(null);
  const [councilMsgs, setCouncilMsgs] = useState(null);
  const [councilFilter, setCouncilFilter] = useState("all");
  const [copiedReq, setCopiedReq] = useState(null);
  const [selReminders, setSelReminders] = useState(() => new Set());
  const [reminderTpl, setReminderTpl] = useState("key_waiting");
  const [sendingReminders, setSendingReminders] = useState(false);
  const [reminderResult, setReminderResult] = useState(null);
  const [cooldownDays, setCooldownDays] = useState(7);
  const [creedList, setCreedList] = useState(null);
  const [creedStats, setCreedStats] = useState(null);
  const [creedReprompting, setCreedReprompting] = useState(false);

  const loadApps = async () => { const { data } = await api.get("/admin/applications"); setApps(data); };
  const loadStats = async () => { try { const { data } = await api.get("/admin/stats"); setStats(data); } catch { /* ignore */ } };
  const loadRequests = async () => { const { data } = await api.get("/admin/invite-requests"); setRequests(data); };
  const loadCouncilMsgs = async () => { const { data } = await api.get("/admin/council-messages"); setCouncilMsgs(data); };
  const resolveCouncilMsg = async (id) => { try { await api.post(`/admin/council-messages/${id}/resolve`); loadCouncilMsgs(); } catch { /* noop */ } };
  const loadCreed = async () => {
    const [{ data: list }, { data: stats }] = await Promise.all([
      api.get("/admin/creed-acceptances"), api.get("/admin/creed-stats"),
    ]);
    setCreedList(list); setCreedStats(stats);
  };
  const repromptCreed = async () => {
    if (!window.confirm("Re-prompt EVERY member to re-accept The Sovereign Creed on their next entry? This bumps the active Creed version.")) return;
    setCreedReprompting(true);
    try { await api.post("/admin/creed/reprompt"); await loadCreed(); }
    finally { setCreedReprompting(false); }
  };

  const exportCreedCsv = () => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = [["Name", "Email", "Accepted At", "Creed Version"],
      ...(creedList || []).map((c) => [c.name, c.email, c.accepted_at || "", c.version || ""])];
    const csv = rows.map((r) => r.map(esc).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `sovereign-creed-acceptances-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };
  const loadInvites = async () => { const { data } = await api.get("/admin/invites"); setInvites(data); };
  const toggleKey = async (code, active) => {
    setInvites((arr) => arr.map((i) => i.code === code ? { ...i, active } : i));
    try { await api.patch(`/admin/invites/${code}`, { active }); }
    catch { setInvites((arr) => arr.map((i) => i.code === code ? { ...i, active: !active } : i)); }
  };
  const loadSettings = async () => { const { data } = await api.get("/settings"); setMemberInvites(data.member_invites_enabled); setProfileReferralCount(data.profile_referral_count_enabled); };
  const toggleMemberInvites = async () => {
    const { data } = await api.post("/admin/settings", { member_invites_enabled: !memberInvites });
    setMemberInvites(data.member_invites_enabled);
  };
  const toggleProfileReferralCount = async () => {
    const { data } = await api.post("/admin/settings", { profile_referral_count_enabled: !profileReferralCount });
    setProfileReferralCount(data.profile_referral_count_enabled);
  };
  const [resendSyncing, setResendSyncing] = useState(false);
  const [resendResult, setResendResult] = useState(null);
  const syncResend = async () => {
    setResendSyncing(true); setResendResult(null);
    try {
      const { data } = await api.post("/admin/resend/sync-all");
      const c = data.synced || {};
      setResendResult(`Synced: ${c.members_founding || 0} members, ${c.pending || 0} pending, ${c.invite_holders || 0} invite holders.`);
    } catch (e) {
      setResendResult(e.response?.data?.detail || "Sync failed. Check Resend configuration.");
    }
    setResendSyncing(false);
  };
  const loadBackupStats = async () => { const { data } = await api.get("/admin/backup/stats"); setBackupStats(data.collections); };
  const loadWatch = async () => {
    let st = null;
    try { const { data: s } = await api.get("/safety/status"); st = s; setSafetyState(s); } catch { /* noop */ }
    const { data } = await api.get("/admin/disturbances");
    let items = data.items || [];
    setWatchSteward(!!data.is_steward);
    if (data.is_steward && st?.exists && st?.provisioned) {
      try {
        if (await unlockSafetyKey()) {
          grantSafetyToStewards();
          items = await Promise.all(items.map(async (d) => ({ ...d, _content: d.sr ? await decryptSafety(d.sr) : null })));
        }
      } catch { /* noop */ }
    }
    // 2-of-N break-glass: load requests, reveal any of MINE that a second steward
    // has already co-signed, and expose the co-sign queue for other stewards.
    if (data.is_steward) {
      try {
        const { data: bg } = await api.get("/safety/break-glass");
        const reqs = bg.requests || [];
        setBreakGlass(reqs);
        const reveals = {};
        for (const r of reqs) {
          if (r.i_requested && r.cosign_count > 0 && r.sr?.v === 2) {
            const txt = await combineBreakGlass(r.sr, r.cosigns);
            if (txt != null) { reveals[r.disturbance_id] = txt; api.post(`/safety/break-glass/${r.id}/revealed`).catch(() => {}); }
          }
        }
        const reqByDist = {};
        for (const r of reqs) { if (r.i_requested) reqByDist[r.disturbance_id] = r; }
        items = items.map((d) => ({
          ...d,
          _content: reveals[d.id] != null ? reveals[d.id] : d._content,
          _bg: reqByDist[d.id] || null,
        }));
        try { const { data: au } = await api.get("/safety/break-glass/audit"); setBgAudit(au.log || []); } catch { /* noop */ }
      } catch { /* noop */ }
    }
    setWatch(items);
    try { const { data: br } = await api.get("/admin/blocked-returns"); setBlockedReturns(br); } catch { /* noop */ }
  };

  const activateSafetyKey = async () => {
    setSafetyBusy(true);
    try { await initSafetyKey(); await loadWatch(); }
    catch (e) { alert("Could not activate the Safety Review key."); }
    finally { setSafetyBusy(false); }
  };
  const startBreakGlass = async (distId) => {
    try { await requestBreakGlass(distId); await loadWatch(); }
    catch (e) { alert(e?.response?.data?.detail || "Could not start the request."); }
  };
  const approveBreakGlass = async (req) => {
    try {
      const ok = await cosignBreakGlass(req);
      if (!ok) { alert("You don't hold a share for this item, or your keys aren't unlocked."); return; }
      await loadWatch();
    } catch (e) { alert(e?.response?.data?.detail || "Could not co-sign."); }
  };
  const cosignQueue = (breakGlass || []).filter((r) => !r.i_requested && !r.i_cosigned);
  const loadDormant = async () => {
    const { data } = await api.get("/admin/dormant-members");
    setDormant(data);
    try { const { data: ns } = await api.get("/admin/dormant-nudge-status"); setNudgeStatus(ns); } catch { /* noop */ }
  };
  const sendReentry = async (id) => {
    setDormantBusy(id);
    try { await api.post(`/admin/dormant-members/${id}/reentry-email`); await loadDormant(); }
    catch (e) { window.alert(e?.response?.data?.detail || "Could not send the email."); }
    finally { setDormantBusy(null); }
  };
  const reentryAll = async () => {
    if (!window.confirm("Send the gentle re-entry email now to every quiet member who is due (respects the 3-month spacing and the 3-nudge cap)?")) return;
    setDormantBusy("all");
    try { const { data } = await api.post("/admin/dormant-members/reentry-all"); window.alert(`Sent ${data.sent} re-entry email${data.sent === 1 ? "" : "s"}.`); await loadDormant(); }
    finally { setDormantBusy(null); }
  };
  const archiveDormant = async (id, mode) => {
    const msg = mode === "keep_directory"
      ? "Archive this account but leave their business/profile posted in the directory? They'll be restored automatically if they ever log back in."
      : "Archive this entire account? They'll be removed from the directory and their posts hidden. They'll be restored automatically if they ever log back in.";
    if (!window.confirm(msg)) return;
    setDormantBusy(id);
    try { await api.post(`/admin/dormant-members/${id}/archive`, { mode }); await loadDormant(); }
    finally { setDormantBusy(null); }
  };
  const restoreDormant = async (id) => {
    setDormantBusy(id);
    try { await api.post(`/admin/dormant-members/${id}/restore`); await loadDormant(); }
    finally { setDormantBusy(null); }
  };
  const loadBeacons = async () => { const { data } = await api.get("/admin/beacons"); setBeacons(data); };
  const approveBeacon = async (id, comped) => { await api.post(`/admin/beacons/${id}/approve`, { comped }); await loadBeacons(); };
  const declineBeacon = async (id) => { if (window.confirm("Decline this Beacon request?")) { await api.post(`/admin/beacons/${id}/decline`); await loadBeacons(); } };
  const archiveLongGone = async (mode) => {
    const n = (dormant || []).filter((d) => d.status !== "archived" && (d.reentry_count || 0) >= 3).length;
    const msg = mode === "keep_directory"
      ? `Archive all ${n} long-gone member${n === 1 ? "" : "s"} but leave their business/profile posted in the directory? They're auto-restored if they ever log back in.`
      : `Archive all ${n} long-gone member${n === 1 ? "" : "s"} (remove from directory, hide posts)? They're auto-restored if they ever log back in.`;
    if (!window.confirm(msg)) return;
    setDormantBusy("longgone");
    try { const { data } = await api.post("/admin/dormant-members/archive-long-gone", { mode }); window.alert(`Archived ${data.archived} member${data.archived === 1 ? "" : "s"}${data.emailed ? ` · ${data.emailed} notified by email` : ""}.`); await loadDormant(); }
    finally { setDormantBusy(null); }
  };
  const loadSupport = async () => {
    const [{ data: cfg }, { data: don }] = await Promise.all([api.get("/admin/donate-config"), api.get("/admin/donations")]);
    setCryptoCfg(cfg); setDonations(don);
  };

  useEffect(() => {
    if (can("applications")) loadApps();
    if (can("requests")) loadRequests();
    if (can("watch") || can("disturbances")) loadCouncilMsgs();
    if (can("requests")) loadCreed();
    if (can("invites")) { loadInvites(); loadSettings(); }
    if (can("backup")) loadBackupStats();
    if (can("watch") || can("disturbances")) loadWatch();
    if (can("applications")) loadDormant();
    if (can("beacon")) loadBeacons();
    if (can("support")) loadSupport();
    if (can("growth")) api.get("/admin/growth-stats").then(({ data }) => setGrowth(data)).catch(() => setGrowth({ total_30d: 0, daily: [], top_referrers: [], by_source: [] }));
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    /* eslint-disable-next-line */
  }, []);

  const saveCrypto = async () => {
    setSavingCrypto(true);
    try {
      const { data } = await api.put("/admin/donate-config", cryptoCfg);
      setCryptoCfg(data);
      setCryptoSaved(true);
      setTimeout(() => setCryptoSaved(false), 2000);
    } catch { alert("Could not save addresses. Please try again."); }
    setSavingCrypto(false);
  };

  const resolveDisturbance = async (id, action) => {
    await api.post(`/admin/disturbances/${id}/${action}`);
    await loadWatch();
  };

  const downloadBackup = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/admin/backup", { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      a.download = `sovereign-signal-backup-${stamp}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { alert("Backup failed. Please try again."); }
    setDownloading(false);
  };

  const decide = async (id, action) => {
    if (action === "approve") { try { await issueEnvelopeFor(id); } catch {} }
    await api.post(`/admin/applications/${id}/${action}`);
    setApps(apps.filter((a) => a.id !== id));
  };

  const syncKeys = async () => {
    setSyncing(true);
    try {
      const { data } = await api.get("/admin/members-needing-keys");
      for (const id of data.ids) { try { await issueEnvelopeFor(id); } catch {} }
    } finally { setSyncing(false); }
  };

  const newInvite = async () => {
    setCreating(true);
    try {
      await api.post("/admin/invites", { label: inviteLabel.trim(), code: inviteCode.trim() });
      setInviteLabel(""); setInviteCode("");
      await loadInvites();
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not create key. Please try again.");
    }
    setCreating(false);
  };

  const saveLabel = async (code) => {
    const label = labelDraft.trim();
    setInvites((arr) => arr.map((i) => i.code === code ? { ...i, label } : i));
    setEditingLabel(null);
    try { await api.patch(`/admin/invites/${code}`, { label }); }
    catch { await loadInvites(); }
  };

  const copyInviteMsg = (code) => {
    const msg = `Join me on Sovereign Signal, a private, encrypted space for sovereign creators. Use key ${code} to enter at https://sovereignsignal.app`;
    navigator.clipboard.writeText(msg);
    setCopiedMsg(code);
    setTimeout(() => setCopiedMsg(null), 1800);
  };

  const approveRequest = async (id) => {
    try {
      const { data } = await api.post(`/admin/invite-requests/${id}/approve`);
      setRequests((arr) => arr.map((r) => r.id === id ? { ...r, status: "approved", approved_code: data.code, emailed: data.emailed } : r));
      loadStats();
    } catch { alert("Could not approve. Please try again."); }
  };

  const declineRequest = async (id) => {
    setRequests((arr) => arr.map((r) => r.id === id ? { ...r, status: "declined" } : r));
    try { await api.post(`/admin/invite-requests/${id}/decline`); loadStats(); }
    catch { await loadRequests(); }
  };

  const dismissRequest = async (id, dismissed) => {
    setRequests((arr) => arr.map((r) => r.id === id ? { ...r, dismissed } : r));
    setSelReminders((s) => { const n = new Set(s); n.delete(id); return n; });
    try { await api.post(`/admin/invite-requests/${id}/dismiss`, { dismissed }); }
    catch { await loadRequests(); }
  };

  const copyRequestInvite = (req) => {
    const msg = `Hi ${req.name}, your request to join Sovereign Signal, the private, encrypted social media alternative, has been accepted. Use your personal key ${req.approved_code} to enter at https://sovereignsignal.app. Welcome.`;
    navigator.clipboard.writeText(msg);
    setCopiedReq(req.id);
    setTimeout(() => setCopiedReq(null), 1800);
  };

  const REMINDER_TEMPLATES = [
    { id: "key_waiting", label: "Your key is waiting" },
    { id: "amy_note", label: "A note from Amy" },
    { id: "final_month", label: "Your key expires soon" },
    { id: "last_call", label: "Last call: invite still open" },
  ];
  const awaitingSignup = (requests || []).filter((r) => r.status === "approved" && r.approved_code && !r.has_account && !r.dismissed && !r.expired);
  const missedInvites = (requests || []).filter((r) => r.status === "approved" && r.approved_code && r.emailed === false && !r.has_account && !r.expired);
  const resendMissed = async () => {
    setResendingMissed(true); setMissedResult(null);
    try {
      const { data } = await api.post("/admin/invite-requests/resend-failed");
      setMissedResult(data);
      await loadRequests();
      loadStats();
    } catch { setMissedResult({ error: true }); }
    setResendingMissed(false);
  };
  const daysSince = (iso) => iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 86400000) : null;
  const onCooldown = (r) => r.reminded_at && cooldownDays > 0 && daysSince(r.reminded_at) < cooldownDays;
  const remindableCount = awaitingSignup.filter((r) => !onCooldown(r)).length;
  const toggleReminderSel = (id) => setSelReminders((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const sendReminders = async ({ all }) => {
    const ids = all ? awaitingSignup.map((r) => r.id) : [...selReminders];
    if (ids.length === 0) return;
    setSendingReminders(true); setReminderResult(null);
    try {
      const body = all
        ? { all: true, template: reminderTpl, cooldown_days: cooldownDays }
        : { ids, template: reminderTpl, cooldown_days: cooldownDays };
      const { data } = await api.post("/admin/invite-requests/remind", body);
      setReminderResult(data);
      setSelReminders(new Set());
      await loadRequests();
      loadStats();
    } catch { setReminderResult({ error: true }); }
    setSendingReminders(false);
  };

  const setShareKey = async (code, val) => {
    setInvites((arr) => arr.map((i) => ({ ...i, is_member_share: i.code === code ? val : (val ? false : i.is_member_share) })));
    try { await api.patch(`/admin/invites/${code}`, { is_member_share: val }); }
    catch { await loadInvites(); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-8">
      <header className="mb-8 flex items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-medium tracking-tight text-[#14A89A]">The Council</h1>
          <p className="font-body text-sm text-zinc-500 mt-1">Steward who enters the signal.</p>
        </div>
        <button onClick={syncKeys} disabled={syncing || !hasNetwork()} data-testid="sync-keys-button" title="Grant the encryption key to approved members who set it up after approval" className="flex items-center gap-2 border border-white/15 text-zinc-300 px-4 py-2.5 font-body text-xs hover:bg-white/5 transition-colors disabled:opacity-40">
          {syncing ? <Loader2 className="animate-spin" size={14} /> : <RefreshCw size={14} />} Sync keys
        </button>
      </header>

      {stats && (
        <div className="grid grid-cols-3 gap-3 mb-8" data-testid="council-stats">
          <div className="bg-[#121212] border border-white/10 px-4 py-4">
            <div className="font-heading text-2xl text-[#14A89A]" data-testid="stat-total-members">{stats.total_members}</div>
            <div className="font-body text-[10px] uppercase tracking-wider text-zinc-500 mt-0.5">Sovereigns</div>
          </div>
          <div className="bg-[#121212] border border-[#14A89A]/25 px-4 py-4">
            <div className="font-heading text-2xl text-white" data-testid="stat-joined-week">+{stats.joined_this_week}</div>
            <div className="font-body text-[10px] uppercase tracking-wider text-zinc-500 mt-0.5">Joined this week</div>
          </div>
          <div className="bg-[#121212] border border-white/10 px-4 py-4">
            <div className="font-heading text-2xl text-[#A855F7]" data-testid="stat-awaiting-signup">{stats.awaiting_signup}</div>
            <div className="font-body text-[10px] uppercase tracking-wider text-zinc-500 mt-0.5">Requested, not applied</div>
          </div>
          <div className="col-span-3 bg-[#121212] border border-white/10 px-4 py-4 flex items-center gap-4" data-testid="stat-mfa-adoption">
            <div className="font-heading text-2xl text-[#14A89A]" data-testid="stat-mfa-pct">{stats.mfa_pct ?? 0}%</div>
            <div className="min-w-0">
              <div className="font-body text-[10px] uppercase tracking-wider text-zinc-500">Use two-step sign-in</div>
              <div className="font-body text-xs text-zinc-400 mt-0.5" data-testid="stat-mfa-members">{stats.mfa_members ?? 0} of {stats.total_members} members</div>
              <div className="font-body text-xs text-zinc-400" data-testid="stat-mfa-creators">Creators: {stats.mfa_creators ?? 0} of {stats.live_creators ?? 0} protected</div>
            </div>
          </div>
        </div>
      )}

      <div className="flex gap-8 border-b border-white/10 mb-8 overflow-x-auto">
        {tabList.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`} className={`shrink-0 pb-3 font-body text-sm tracking-wider uppercase transition-colors ${tab === t ? "text-white border-b-2 border-[#14A89A]" : "text-zinc-500 hover:text-white border-b-2 border-transparent"}`}>
            {t === "applications" ? "Applications" : t === "requests" ? "Requests" : t === "watch_inbox" ? "The Watch Inbox" : t === "creed" ? "The Creed" : t === "invites" ? "Signal Keys" : t === "quiet" ? "The Quiet" : t === "beacon" ? "Beacons" : t === "support" ? "Support" : t === "council" ? "Council" : t === "tips" ? "Tips" : t === "stream" ? "Stream" : t === "growth" ? "Growth" : t === "brand" ? "Refresh" : "Backup"}
            {t === "requests" && requests && requests.filter((r) => r.status === "pending").length > 0 && (
              <span className="ml-2 bg-[#A855F7] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full" data-testid="requests-pending-badge">{requests.filter((r) => r.status === "pending").length}</span>
            )}
            {t === "watch_inbox" && (((councilMsgs || []).filter((m) => m.status !== "resolved").length) + ((watch || []).filter((d) => d.status === "open").length)) > 0 && (
              <span className="ml-2 bg-[#A855F7] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full" data-testid="watch-inbox-badge">{((councilMsgs || []).filter((m) => m.status !== "resolved").length) + ((watch || []).filter((d) => d.status === "open").length)}</span>
            )}
            {t === "beacon" && beacons && beacons.filter((b) => b.status === "pending").length > 0 && (
              <span className="ml-2 bg-[#A855F7] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full" data-testid="beacon-pending-badge">{beacons.filter((b) => b.status === "pending").length}</span>
            )}
          </button>
        ))}
      </div>

      {tab === "growth" && (
        <div data-testid="growth-tab">
          {growth === null ? <SignalPulse size={40} /> : (
            <div className="space-y-8">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="font-body text-[10px] tracking-widest uppercase text-zinc-500 mb-1">Signups · last 30 days</div>
                  <div className="font-heading text-3xl text-white" data-testid="growth-total-30d">{growth.total_30d}</div>
                </div>
                <button onClick={copyGrowthSummary} data-testid="growth-copy-summary" className="flex items-center gap-2 border border-white/15 text-zinc-300 px-4 py-2.5 font-body text-xs hover:border-[#14A89A] hover:text-white transition-colors shrink-0">
                  {summaryCopied ? <Check size={14} className="text-[#14A89A]" /> : <Copy size={14} />} {summaryCopied ? "Copied" : "Copy monthly summary"}
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {isFounder && <button onClick={() => downloadOverview()} data-testid="growth-download-overview" className="flex items-center gap-2 border border-[#A855F7]/40 text-[#A855F7] px-4 py-2.5 font-body text-xs hover:bg-[#A855F7]/10 transition-colors">
                  <Download size={14} /> Download the partner overview (PDF)
                </button>}
                <button onClick={() => downloadOverview("/admin/creator-guide-pdf", "Beacon-Blueprint-v2-Council.pdf")} data-testid="growth-download-creator-guide" className="flex items-center gap-2 border border-[#14A89A]/40 text-[#14A89A] px-4 py-2.5 font-body text-xs hover:bg-[#14A89A]/10 transition-colors">
                  <Download size={14} /> Download the Beacon Blueprint, Council copy (PDF)
                </button>
              </div>

              {growth.top_of_month && (
                <div className="border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-5 flex items-center gap-4" data-testid="growth-top-of-month">
                  <Star size={22} className="text-[#D4AF37] shrink-0" />
                  <div>
                    <div className="font-body text-[10px] tracking-widest uppercase text-[#D4AF37] mb-1">Top Sovereign this month</div>
                    <div className="font-heading text-lg text-white" data-testid="growth-top-of-month-name">
                      {growth.top_of_month.name || "A sovereign"}
                      <span className="text-zinc-500 text-sm font-body"> · {growth.top_of_month.count} {growth.top_of_month.count === 1 ? "referral" : "referrals"}</span>
                    </div>
                  </div>
                </div>
              )}

              {isFounder && <div className="border border-white/10 bg-[#121212] p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="growth-announce-panel">
                <div>
                  <div className="font-body text-sm text-white mb-1">Post the monthly Signal to the Stream</div>
                  <p className="font-body text-xs text-zinc-500 max-w-md">A names-free, celebratory growth note (new members + top referral count) pinned to the top of the Conscious Stream for all members. Re-posting updates this month's card.</p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {announceState && announceState !== "posting" && <span className="font-body text-xs text-[#14A89A]" data-testid="growth-announce-status">{announceState}</span>}
                  <button onClick={postAnnouncement} disabled={announceState === "posting"} data-testid="growth-announce-button" className="flex items-center gap-2 border border-[#D4AF37]/50 text-[#D4AF37] px-4 py-2.5 font-body text-xs hover:bg-[#D4AF37]/10 transition-colors disabled:opacity-40">
                    {announceState === "posting" ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Post to Stream
                  </button>
                </div>
              </div>}

              <div className="bg-[#121212] border border-white/10 p-5">
                <div className="font-body text-[10px] tracking-widest uppercase text-zinc-500 mb-4">Daily signups</div>
                <div className="flex items-end gap-1 h-28" data-testid="growth-daily">
                  {(() => {
                    const max = Math.max(1, ...growth.daily.map((d) => d.count));
                    return growth.daily.map((d) => (
                      <div key={d.date} title={`${d.date}: ${d.count}`} className="flex-1 flex flex-col justify-end group">
                        <div className="w-full bg-[#14A89A]/80 group-hover:bg-[#2BC4B5] transition-colors" style={{ height: `${Math.round((d.count / max) * 100)}%`, minHeight: d.count ? "3px" : "0" }} />
                      </div>
                    ));
                  })()}
                </div>
                <div className="flex justify-between mt-2 font-body text-[10px] text-zinc-600">
                  <span>{growth.daily[0]?.date?.slice(5)}</span>
                  <span>{growth.daily[growth.daily.length - 1]?.date?.slice(5)}</span>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-[#121212] border border-white/10 p-5">
                  <div className="font-body text-[10px] tracking-widest uppercase text-zinc-500 mb-4">Top referrers</div>
                  {growth.top_referrers.length === 0 ? (
                    <p className="font-body text-sm text-zinc-600" data-testid="growth-referrers-empty">No member referrals yet.</p>
                  ) : (
                    <div className="space-y-2" data-testid="growth-referrers">
                      {growth.top_referrers.map((r) => (
                        <div key={r.id} className="flex items-center justify-between gap-3">
                          <span className="font-body text-sm text-white truncate">{r.name || "A sovereign"}</span>
                          <span className="font-heading text-sm text-[#14A89A] shrink-0">{r.count}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="bg-[#121212] border border-white/10 p-5">
                  <div className="font-body text-[10px] tracking-widest uppercase text-zinc-500 mb-4">Signups by source</div>
                  <div className="space-y-2" data-testid="growth-sources">
                    {growth.by_source.map((s) => (
                      <div key={s.source} className="flex items-center justify-between gap-3">
                        <span className="font-body text-sm text-zinc-300 truncate">{s.source}</span>
                        <span className="font-heading text-sm text-[#A855F7] shrink-0">{s.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <p className="font-body text-[11px] text-zinc-600">Derived only from signup metadata, arrival source (UTM) and who referred whom. No per-visit tracking, no content. Owner-only.</p>
            </div>
          )}
        </div>
      )}

      {tab === "applications" && (
        apps === null ? <SignalPulse size={40} /> :
        apps.length === 0 ? <p className="font-body text-sm text-zinc-500">No pending applications.</p> :
        <div className="space-y-4">
          {apps.map((a) => (
            <div key={a.id} className="bg-[#121212] border border-white/10 p-6" data-testid="application-card">
              <div className="flex items-start gap-4">
                <Avatar name={a.name} size={48} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <div className="font-heading text-base text-white">{a.name}</div>
                      <div className="font-body text-xs text-zinc-500">{a.email}</div>
                    </div>
                    <CategoryPill cat={a.contribution_type} />
                  </div>
                  <p className="font-body text-sm text-zinc-300 mt-3">{a.headline}</p>
                  <p className="font-body text-xs text-zinc-500 mt-2 leading-relaxed">{a.bio}</p>
                  <div className="grid sm:grid-cols-2 gap-3 mt-3">
                    <div><span className="text-[10px] uppercase tracking-wider text-zinc-600">Offers</span><p className="font-body text-xs text-zinc-400">{a.offering}</p></div>
                    <div><span className="text-[10px] uppercase tracking-wider text-zinc-600">Seeks</span><p className="font-body text-xs text-zinc-400">{a.seeking}</p></div>
                  </div>
                  <div className="flex gap-3 mt-5">
                    <button onClick={() => decide(a.id, "approve")} data-testid={`approve-${a.id}`} className="flex items-center gap-2 bg-[#14A89A] text-[#0A0A0A] px-5 py-2.5 font-body text-sm font-medium hover:bg-[#2BC4B5] transition-colors"><Check size={16} /> Admit</button>
                    <button onClick={() => decide(a.id, "reject")} data-testid={`reject-${a.id}`} className="flex items-center gap-2 border border-white/15 text-zinc-300 px-5 py-2.5 font-body text-sm hover:bg-white/5 transition-colors"><X size={16} /> Decline</button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "requests" && (
        <div>
          <p className="font-body text-xs text-zinc-500 mb-6">Requests from the public <span className="text-zinc-300">/welcome</span> page. A private key is <span className="text-zinc-300">auto-emailed</span> the moment someone requests, your real screening happens when their full <span className="text-zinc-300">Application</span> arrives. This is your log of who requested and why.</p>

          {missedInvites.length > 0 && (
            <div className="mb-6 border border-red-400/30 bg-red-400/[0.06] p-5" data-testid="missed-invites-panel">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="font-heading text-base text-red-200">Invite emails that didn't send · {missedInvites.length}</div>
                  <p className="font-body text-xs text-zinc-400 mt-1 max-w-md">These keys were generated but the email failed (e.g. during an email-provider outage). Re-send now to recover them, each person receives their original key.</p>
                </div>
                <button onClick={resendMissed} disabled={resendingMissed} data-testid="resend-missed-button" className="flex items-center gap-2 bg-red-400 text-[#0A0A0A] px-4 py-2.5 font-body text-xs font-medium hover:bg-red-300 transition-colors disabled:opacity-40 shrink-0">
                  {resendingMissed ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Re-send missed invites ({missedInvites.length})
                </button>
              </div>
              {missedResult && (
                <p className="font-body text-[11px] mt-3" data-testid="missed-result">
                  {missedResult.error
                    ? <span className="text-red-400">Something went wrong re-sending, check that the email key is configured in production.</span>
                    : <span className="text-[#14A89A]">✓ Re-sent {missedResult.sent} of {missedResult.attempted}{missedResult.failed > 0 ? ` · ${missedResult.failed} still failing (key may still be invalid)` : ""}</span>}
                </p>
              )}
            </div>
          )}

          {awaitingSignup.length > 0 && (
            <div className="mb-8 border border-[#D4AF37]/25 bg-[#D4AF37]/5 p-5" data-testid="awaiting-signup-panel">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="font-heading text-base text-[#F3E5AB]">Awaiting sign-up · {awaitingSignup.length}</div>
                  <p className="font-body text-xs text-zinc-400 mt-1 max-w-md">Sent a key but never came back to apply. Send a reminder to nudge them to finish their application.</p>
                  <FunnelSummary rows={awaitingSignup} />
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <select value={reminderTpl} onChange={(e) => setReminderTpl(e.target.value)} data-testid="reminder-template-select" className="bg-[#0A0A0A] border border-white/15 text-zinc-200 text-xs px-3 py-2.5 focus:outline-none focus:border-[#14A89A]">
                    {REMINDER_TEMPLATES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                  </select>
                  <button onClick={() => sendReminders({ all: false })} disabled={sendingReminders || selReminders.size === 0} data-testid="remind-selected-button" className="flex items-center gap-2 border border-white/15 text-zinc-200 px-4 py-2.5 font-body text-xs hover:bg-white/5 transition-colors disabled:opacity-40">
                    <Send size={14} /> Remind selected ({selReminders.size})
                  </button>
                  <button onClick={() => sendReminders({ all: true })} disabled={sendingReminders || remindableCount === 0} data-testid="remind-all-button" className="flex items-center gap-2 bg-[#D4AF37] text-[#0A0A0A] px-4 py-2.5 font-body text-xs font-medium hover:bg-[#F3E5AB] transition-colors disabled:opacity-40">
                    {sendingReminders ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Remind all ({remindableCount})
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-3 font-body text-[11px] text-zinc-400">
                <span>Don't re-remind anyone contacted in the last</span>
                <input type="number" min="0" max="365" value={cooldownDays} onChange={(e) => setCooldownDays(Math.max(0, parseInt(e.target.value) || 0))} data-testid="cooldown-days-input" className="w-14 bg-[#0A0A0A] border border-white/15 text-zinc-200 text-xs px-2 py-1.5 text-center focus:outline-none focus:border-[#14A89A]" />
                <span>days.</span>
              </div>
              {reminderResult && (
                <p className="font-body text-[11px] mt-3" data-testid="reminder-result">
                  {reminderResult.error
                    ? <span className="text-red-400">Something went wrong sending reminders.</span>
                    : <span className="text-[#14A89A]">✓ Sent {reminderResult.sent}{reminderResult.failed > 0 ? ` · ${reminderResult.failed} failed` : ""}{reminderResult.skipped_recent > 0 ? ` · ${reminderResult.skipped_recent} skipped (recently reminded)` : ""}{reminderResult.skipped > 0 ? ` · ${reminderResult.skipped} skipped (already joined)` : ""}</span>}
                </p>
              )}
              <div className="mt-4 space-y-2">
                {awaitingSignup.map((r) => {
                  const cd = onCooldown(r);
                  const d = daysSince(r.reminded_at);
                  return (
                  <label key={r.id} className={`flex items-center gap-3 bg-[#0A0A0A] border border-white/10 px-4 py-3 transition-colors ${cd ? "opacity-60" : "cursor-pointer hover:border-white/20"}`} data-testid={`awaiting-row-${r.id}`}>
                    <input type="checkbox" checked={selReminders.has(r.id)} disabled={cd} onChange={() => toggleReminderSel(r.id)} data-testid={`awaiting-check-${r.id}`} className="accent-[#D4AF37] w-4 h-4 shrink-0 disabled:cursor-not-allowed" />
                    <div className="min-w-0 flex-1">
                      <div className="font-body text-sm text-white truncate">{r.name}</div>
                      <div className="font-body text-xs text-zinc-500 truncate">{r.email}</div>
                      <div className="mt-1.5"><FunnelSteps r={r} prefix="awaiting-funnel" /></div>
                    </div>
                    {r.reminder_count > 0 && (
                      <span className="shrink-0 font-body text-[10px] text-[#D4AF37] border border-[#D4AF37]/30 px-2 py-1" data-testid={`reminded-badge-${r.id}`}>
                        Reminded ×{r.reminder_count}{d != null ? ` · ${d === 0 ? "today" : `${d}d ago`}` : ""}
                      </span>
                    )}
                    {cd && <span className="shrink-0 font-body text-[10px] text-zinc-500" data-testid={`cooldown-tag-${r.id}`}>on cooldown</span>}
                    <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); dismissRequest(r.id, true); }} data-testid={`awaiting-dismiss-${r.id}`} title="They've already joined (with a different email or key), remove from this list. Stays in the log below." className="shrink-0 flex items-center gap-1.5 border border-[#14A89A]/30 text-[#14A89A] px-2.5 py-1.5 font-body text-[10px] uppercase tracking-wider hover:bg-[#14A89A]/10 transition-colors">
                      <Check size={12} /> Mark joined
                    </button>
                  </label>
                  );
                })}
              </div>
            </div>
          )}
          {requests === null ? <SignalPulse size={40} /> :
           requests.length === 0 ? <p className="font-body text-sm text-zinc-500">No invite requests yet.</p> :
          <div className="space-y-4">
            {requests.map((r) => (
              <div key={r.id} className="bg-[#121212] border border-white/10 p-5" data-testid="invite-request-card">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-heading text-base text-white">{r.name}</div>
                    <a href={`mailto:${r.email}`} className="font-body text-xs text-[#14A89A] hover:text-[#2BC4B5] break-all">{r.email}</a>
                    {r.created_at && (
                      <div className="font-body text-[11px] text-zinc-500 mt-1" data-testid={`request-date-${r.id}`}>
                        Requested {new Date(r.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
                      </div>
                    )}
                  </div>
                  {r.status === "pending" && <span className="shrink-0 text-[10px] uppercase tracking-wider text-[#A855F7] border border-[#A855F7]/40 px-2 py-1">Pending</span>}
                  {r.status === "approved" && <span className="shrink-0 text-[10px] uppercase tracking-wider text-[#14A89A] border border-[#14A89A]/40 px-2 py-1">Approved</span>}
                  {r.status === "declined" && <span className="shrink-0 text-[10px] uppercase tracking-wider text-zinc-500 border border-white/15 px-2 py-1">Declined</span>}
                </div>
                <p className="font-body text-sm text-zinc-300 mt-3 leading-relaxed break-words">{r.creating}</p>

                {r.status === "pending" && (
                  <div className="flex gap-3 mt-5">
                    <button onClick={() => approveRequest(r.id)} data-testid={`request-approve-${r.id}`} className="flex items-center gap-2 bg-[#14A89A] text-[#0A0A0A] px-5 py-2.5 font-body text-sm font-medium hover:bg-[#2BC4B5] transition-colors"><Check size={16} /> Approve & forge key</button>
                    <button onClick={() => declineRequest(r.id)} data-testid={`request-decline-${r.id}`} className="flex items-center gap-2 border border-white/15 text-zinc-300 px-5 py-2.5 font-body text-sm hover:bg-white/5 transition-colors"><X size={16} /> Decline</button>
                  </div>
                )}
                {r.status === "approved" && r.approved_code && (
                  <div className="mt-5 border border-[#14A89A]/25 bg-[#0A0A0A] p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="font-body text-[10px] uppercase tracking-wider text-zinc-500">Their private key</div>
                        <div className="font-heading text-lg text-[#14A89A] tracking-[0.15em]" data-testid={`request-code-${r.id}`}>{r.approved_code}</div>
                      </div>
                      <button onClick={() => copyRequestInvite(r)} data-testid={`request-copy-${r.id}`} className="flex items-center gap-2 border border-white/15 text-zinc-300 px-4 py-2.5 font-body text-xs hover:bg-white/5 transition-colors">
                        {copiedReq === r.id ? <><Check size={14} className="text-[#14A89A]" /> Copied</> : <><Send size={14} /> Copy invite message</>}
                      </button>
                    </div>
                    <p className="font-body text-[11px] text-zinc-600 mt-3">
                      {r.emailed
                        ? `✓ Invite emailed automatically to ${r.email}. You can also copy the message to send it another way.`
                        : `Auto-email didn't send (domain may not be verified yet), copy this message and send it to ${r.email} yourself.`}
                    </p>
                    <p className="font-body text-[11px] mt-2" data-testid={`request-signup-status-${r.id}`}>
                      {r.has_account
                        ? <span className="text-[#14A89A]">✓ Signed up, account created.</span>
                        : r.expired
                          ? <span className="text-zinc-500">Key expired{r.expired_at ? ` on ${new Date(r.expired_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}` : ""} · never applied. They can request a new key to restart.</span>
                        : r.dismissed
                          ? <span className="text-[#14A89A]">✓ Marked as joined{r.dismissed_at ? ` on ${new Date(r.dismissed_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}` : ""}. <button type="button" onClick={() => dismissRequest(r.id, false)} data-testid={`request-undismiss-${r.id}`} className="text-zinc-400 underline hover:text-white transition-colors">Undo</button></span>
                          : r.reminder_count > 0
                            ? <span className="text-[#D4AF37]">Not signed up yet · reminded ×{r.reminder_count}.</span>
                            : <span className="text-zinc-500">Not signed up yet.</span>}
                    </p>
                    {!r.expired && !r.dismissed && <div className="mt-2"><FunnelSteps r={r} /></div>}
                  </div>
                )}
              </div>
            ))}
          </div>}
        </div>
      )}

      {tab === "watch_inbox" && (
        <div data-testid="watch-inbox-header">
          {blockedReturns && blockedReturns.total > 0 && (
            <div data-testid="blocked-returns-card" className="mb-6 border border-white/10 bg-[#121212] px-5 py-4 flex items-center gap-4">
              <Ban size={18} className="text-zinc-500 shrink-0" />
              <div className="flex-1">
                <p className="font-body text-xs tracking-[0.2em] uppercase text-zinc-500">Blocked return attempts</p>
                <p className="font-body text-sm text-zinc-300 mt-1">
                  <span data-testid="blocked-returns-30d" className="text-white">{blockedReturns.last_30d}</span> in the last 30 days
                  {blockedReturns.distinct_30d > 0 && <span className="text-zinc-500"> · from {blockedReturns.distinct_30d} distinct {blockedReturns.distinct_30d === 1 ? "person" : "people"}</span>}
                  <span className="text-zinc-500"> · {blockedReturns.total} all-time</span>
                </p>
              </div>
            </div>
          )}
          <div className="flex gap-2 mb-6 flex-wrap">
            {[["inbox", "Inbox"], ["flags", "Flags"]].map(([k, l]) => {
              const badge = k === "inbox"
                ? (councilMsgs ? councilMsgs.filter((m) => m.status !== "resolved").length : 0)
                : (watch ? watch.filter((d) => d.status === "open").length : 0);
              return (
                <button key={k} onClick={() => setWatchSection(k)} data-testid={`watch-section-${k}`} className={`px-4 py-2 font-body text-xs border transition-colors ${watchSection === k ? "border-[#14A89A] text-[#14A89A]" : "border-white/15 text-zinc-400 hover:text-white"}`}>
                  {l}{badge > 0 ? <span className="ml-1.5 text-[#D4AF37]">· {badge}</span> : ""}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {tab === "watch_inbox" && watchSection === "inbox" && (
        <div data-testid="council-inbox">
          <p className="font-body text-sm text-zinc-400 mb-6">Messages sent to the Council via <span className="text-white">Contact the Council</span>: disturbances in the field and general inquiries. Reply as The Council (the member sees “The Council”) or by Exchange for fully private matters; mark resolved when handled.</p>
          <div className="flex gap-2 mb-6 flex-wrap">
            {[["all", "All"], ["disturbance", "Disturbances in the Field"], ["inquiry", "Questions, ideas & concerns"]].map(([k, l]) => (
              <button key={k} onClick={() => setCouncilFilter(k)} data-testid={`council-filter-${k}`} className={`px-3 py-1.5 font-body text-xs border transition-colors ${councilFilter === k ? "border-[#14A89A] text-[#14A89A]" : "border-white/15 text-zinc-400 hover:text-white"}`}>{l}</button>
            ))}
          </div>
          {councilMsgs === null ? <SignalPulse size={40} /> : (
            councilMsgs.filter((m) => councilFilter === "all" || m.kind === councilFilter).length === 0 ? (
              <p className="font-body text-sm text-zinc-600 py-8">Nothing here yet.</p>
            ) : (
              <div className="space-y-3">
                {councilMsgs.filter((m) => councilFilter === "all" || m.kind === councilFilter).map((m) => (
                  <div key={m.id} data-testid={`council-msg-${m.id}`} className={`border p-4 ${m.status === "resolved" ? "border-white/8 opacity-60" : m.kind === "disturbance" ? "border-[#A855F7]/40" : "border-white/15"}`}>
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <span className={`font-body text-[10px] tracking-[0.2em] uppercase ${m.kind === "disturbance" ? "text-[#A855F7]" : "text-[#14A89A]"}`}>{m.kind === "disturbance" ? "⚠ Disturbance in the Field" : "✦ Question / Idea / Concern"}</span>
                      <span className="font-body text-[10px] text-zinc-600">{m.created_at ? new Date(m.created_at).toLocaleString() : ""}</span>
                    </div>
                    <p className="font-body text-sm text-zinc-200 whitespace-pre-wrap mb-3">{m.message}</p>
                    <CouncilThreadReply m={m} onReplied={(id, rep) => setCouncilMsgs((xs) => xs.map((x) => (x.id === id ? { ...x, replies: [...(x.replies || []), rep] } : x)))} />
                    <div className="h-3" />
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <span className="font-body text-xs text-zinc-400">From <span className="text-white">{m.sender_name}</span></span>
                      <div className="flex items-center gap-4">
                        {m.sender_id && <a href={`/messages/${m.sender_id}`} data-testid={`council-reply-${m.id}`} className="inline-flex items-center gap-1.5 border border-[#14A89A]/60 text-[#14A89A] hover:bg-[#14A89A] hover:text-[#0A0A0A] px-3 py-1.5 font-body text-xs transition-colors"><MessageSquare size={13} /> Reply with Exchange</a>}
                        <button onClick={() => resolveCouncilMsg(m.id)} data-testid={`council-resolve-${m.id}`} className={`font-body text-xs ${m.status === "resolved" ? "text-zinc-500 hover:text-white" : "text-zinc-400 hover:text-[#14A89A]"}`}>{m.status === "resolved" ? `Resolved by ${m.resolved_by_name || "Council"} · reopen` : "Mark resolved"}</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      )}

      {tab === "creed" && (
        <div data-testid="creed-tab">
          <div className="flex items-start justify-between gap-3 mb-6">
            <p className="font-body text-xs text-zinc-500 max-w-md">A record of every member who has accepted <span className="text-zinc-300">The Sovereign Creed</span>: name, when they agreed, and the version they signed.</p>
            {creedList && creedList.length > 0 && (
              <button onClick={exportCreedCsv} data-testid="creed-export-csv" className="flex items-center gap-1.5 border border-white/15 text-zinc-300 px-3 py-2 font-body text-xs hover:border-[#14A89A] hover:text-white transition-colors shrink-0">
                <Download size={14} /> Export CSV
              </button>
            )}
          </div>
          {creedStats && (
            <div className="bg-[#121212] border border-white/10 p-5 mb-6" data-testid="creed-stats">
              <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                  <div className="font-body text-[10px] tracking-widest uppercase text-zinc-500 mb-1">Accepted current version (v{creedStats.active_version})</div>
                  <div className="font-heading text-3xl text-white" data-testid="creed-stats-count">
                    {creedStats.accepted}<span className="text-zinc-600 text-xl"> / {creedStats.total_members}</span>
                  </div>
                  {creedStats.total_members > creedStats.accepted && (
                    <div className="font-body text-xs text-[#D4AF37] mt-1" data-testid="creed-stats-pending">{creedStats.total_members - creedStats.accepted} member{creedStats.total_members - creedStats.accepted === 1 ? "" : "s"} yet to accept</div>
                  )}
                </div>
                <button onClick={repromptCreed} disabled={creedReprompting} data-testid="creed-reprompt-button" className="flex items-center gap-2 border border-[#D4AF37]/40 text-[#D4AF37] px-4 py-2.5 font-body text-xs hover:bg-[#D4AF37]/10 transition-colors shrink-0 disabled:opacity-40">
                  {creedReprompting ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Re-prompt everyone
                </button>
              </div>
              <div className="w-full h-1.5 bg-white/5 mt-4 overflow-hidden">
                <div className="h-full bg-[#14A89A] transition-all" style={{ width: `${creedStats.total_members ? Math.round((creedStats.accepted / creedStats.total_members) * 100) : 0}%` }} />
              </div>
              <p className="font-body text-[11px] text-zinc-600 mt-3">"Re-prompt everyone" bumps the active version so all members must re-accept the Creed on their next entry. Use after editing the Creed text.</p>
            </div>
          )}
          {creedList === null ? <SignalPulse size={40} /> : creedList.length === 0 ? (
            <p className="font-body text-sm text-zinc-600">No acceptances recorded yet.</p>
          ) : (
            <div className="space-y-2">
              <div className="font-body text-[10px] tracking-widest uppercase text-zinc-500 mb-1">{creedList.length} accepted</div>
              {creedList.map((c) => (
                <div key={c.id} className="flex items-center gap-3 bg-[#121212] border border-white/10 px-4 py-3" data-testid={`creed-row-${c.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-body text-sm text-white truncate">{c.name}</div>
                    <div className="font-body text-xs text-zinc-500 truncate">{c.email}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-body text-xs text-zinc-300">{c.accepted_at ? new Date(c.accepted_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "-"}</div>
                    <div className="font-body text-[10px] text-[#14A89A]">Creed v{c.version || "-"}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}


      {tab === "invites" && (
        <div>
          <div className="flex items-center justify-between gap-4 border border-white/10 bg-[#121212] px-5 py-4 mb-6" data-testid="member-invites-toggle-row">
            <div className="min-w-0">
              <p className="font-body text-sm text-white">Allow members to create invite links</p>
              <p className="font-body text-xs text-zinc-500 mt-0.5">When off, only Council keys work and the member invite panel is hidden.</p>
            </div>
            <button onClick={toggleMemberInvites} data-testid="member-invites-toggle" role="switch" aria-checked={!!memberInvites} className={`relative shrink-0 w-12 h-6 rounded-full transition-colors ${memberInvites ? "bg-[#14A89A]" : "bg-zinc-700"}`}>
              <span className={`absolute top-0.5 h-5 w-5 bg-white rounded-full transition-all ${memberInvites ? "left-[26px]" : "left-0.5"}`} />
            </button>
          </div>
          <div className="flex items-center justify-between gap-4 border border-white/10 bg-[#121212] px-5 py-4 mb-6" data-testid="resend-sync-row">
            <div className="min-w-0">
              <p className="font-body text-sm text-white">Sync members to Resend</p>
              <p className="font-body text-xs text-zinc-500 mt-0.5 leading-relaxed">Pushes everyone into the right Resend segment (Invite Holders, Pending, Members) for your email campaigns. Runs automatically on status changes and nightly; use this to backfill or refresh now.</p>
              {resendResult && <p className="font-body text-xs text-[#14A89A] mt-1.5" data-testid="resend-sync-result">{resendResult}</p>}
            </div>
            <button onClick={syncResend} disabled={resendSyncing} data-testid="resend-sync-button" className="flex items-center justify-center gap-2 shrink-0 bg-[#14A89A] text-[#0A0A0A] px-4 py-2.5 font-body text-sm font-medium hover:bg-[#2BC4B5] transition-colors disabled:opacity-40">
              {resendSyncing ? <Loader2 className="animate-spin" size={16} /> : <RefreshCw size={16} />} Sync now
            </button>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 mb-2">
            <input value={inviteCode} onChange={(e) => setInviteCode(e.target.value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase())} placeholder="Custom key word (optional, e.g. ASCENSION)" data-testid="invite-code-input" className="flex-1 bg-[#0A0A0A] border border-white/15 px-4 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#14A89A] font-body tracking-wider" />
            <input value={inviteLabel} onChange={(e) => setInviteLabel(e.target.value)} placeholder="Label (optional, e.g. 'Instagram launch')" data-testid="invite-label-input" className="flex-1 bg-[#0A0A0A] border border-white/15 px-4 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#14A89A] font-body" />
            <button onClick={newInvite} disabled={creating} data-testid="create-invite-button" className="flex items-center justify-center gap-2 bg-[#14A89A] text-[#0A0A0A] px-5 py-2.5 font-body text-sm font-medium hover:bg-[#2BC4B5] transition-colors disabled:opacity-40 shrink-0">
              {creating ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />} Forge key
            </button>
          </div>
          <p className="font-body text-xs text-zinc-600 mb-6">Type your own word for the key, or leave it blank for a random coded key.</p>
          {invites === null ? <SignalPulse size={40} /> : (
            <>
              <p className="font-body text-xs text-zinc-500 mb-4" data-testid="invite-total">{invites.reduce((s, i) => s + i.joins, 0)} members have joined across {invites.length} key{invites.length !== 1 ? "s" : ""}.</p>
              {(() => {
                const renderRow = (i) => (
                  <div key={i.code} className="bg-[#121212] border border-white/10" data-testid="invite-row">
                    <div className="flex items-center justify-between px-5 py-4 gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-3">
                          <span className={`font-heading text-lg tracking-[0.2em] ${i.active ? "text-[#14A89A]" : "text-zinc-600 line-through"}`}>{i.code}</span>
                          <button onClick={() => navigator.clipboard.writeText(i.code)} title="Copy" className="text-zinc-500 hover:text-white"><Copy size={15} /></button>
                          {!i.active && <span className="font-body text-[9px] uppercase tracking-wider text-zinc-500 border border-white/15 px-1.5 py-0.5">Off</span>}
                          <button onClick={() => copyInviteMsg(i.code)} data-testid={`invite-copy-msg-${i.code}`} title="Copy a ready-to-share invite message" className="font-body text-[11px] flex items-center gap-1 text-zinc-500 hover:text-[#14A89A] transition-colors">
                            {copiedMsg === i.code ? <><Check size={13} className="text-[#14A89A]" /> Copied</> : <><Send size={13} /> Invite text</>}
                          </button>
                        </div>
                        {editingLabel === i.code ? (
                          <div className="flex items-center gap-2 mt-1.5">
                            <input value={labelDraft} autoFocus onChange={(e) => setLabelDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && saveLabel(i.code)} data-testid={`label-input-${i.code}`} placeholder="Label this key" className="bg-[#0A0A0A] border border-white/15 px-2.5 py-1 text-xs text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#14A89A] font-body" />
                            <button onClick={() => saveLabel(i.code)} data-testid={`label-save-${i.code}`} className="font-body text-[11px] bg-[#14A89A] text-[#0A0A0A] font-medium px-2.5 py-1 hover:bg-[#2BC4B5] transition-colors">Save</button>
                            <button onClick={() => setEditingLabel(null)} className="font-body text-[11px] text-zinc-400 hover:text-white">Cancel</button>
                          </div>
                        ) : (
                          <button onClick={() => { setLabelDraft((i.label || "").replace(" \u2014 ", ": ")); setEditingLabel(i.code); }} data-testid={`label-edit-${i.code}`} className="group/lbl flex items-center gap-1.5 mt-0.5 text-zinc-500 hover:text-white transition-colors max-w-full">
                            <span className="font-body text-xs truncate">{(i.label || "Add a label").replace(" \u2014 ", ": ")}{i.label && i.created_by_name ? ` · by ${i.created_by_name}` : ""}</span>
                            <Pencil size={11} className="opacity-0 group-hover/lbl:opacity-100 transition-opacity shrink-0" />
                          </button>
                        )}
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <button onClick={() => setShareKey(i.code, !i.is_member_share)} data-testid={`invite-share-${i.code}`} title={i.is_member_share ? "This is the member share key: tap to unset" : "Set as the key members can share with friends"} className={`shrink-0 transition-colors ${i.is_member_share ? "text-[#D4AF37]" : "text-zinc-600 hover:text-[#D4AF37]"}`}>
                          <Star size={17} className={i.is_member_share ? "fill-[#D4AF37]" : ""} />
                        </button>
                        <button onClick={() => toggleKey(i.code, !i.active)} data-testid={`invite-toggle-${i.code}`} role="switch" aria-checked={!!i.active} title={i.active ? "Active: tap to retire" : "Off: tap to activate"} className={`relative shrink-0 w-11 h-6 rounded-full transition-colors ${i.active ? "bg-[#14A89A]" : "bg-zinc-700"}`}>
                          <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${i.active ? "translate-x-5" : "translate-x-0"}`} />
                        </button>
                        <button onClick={() => setExpandedKey(expandedKey === i.code ? null : i.code)} disabled={i.joins === 0} data-testid={`invite-joins-${i.code}`} className={`flex items-center gap-1.5 font-body text-xs px-3 py-1.5 border transition-colors ${i.joins > 0 ? "border-[#A855F7]/40 text-[#A855F7] hover:bg-[#A855F7]/10" : "border-white/10 text-zinc-600"}`}>
                          <Users size={13} /> {i.joins} joined
                        </button>
                      </div>
                    </div>
                    {expandedKey === i.code && i.members.length > 0 && (
                      <div className="border-t border-white/10 px-5 py-3 space-y-2" data-testid={`invite-members-${i.code}`}>
                        {i.members.map((m) => (
                          <div key={m.id} className="flex items-center justify-between">
                            <span className="font-body text-sm text-zinc-300">{m.name}</span>
                            <span className={`font-body text-[10px] uppercase tracking-wider px-2 py-0.5 border ${m.status === "approved" ? "border-[#14A89A]/40 text-[#14A89A]" : m.status === "banned" ? "border-red-500/40 text-red-400" : "border-white/15 text-zinc-500"}`}>{m.status}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
                const isAuto = (i) => i.created_by_role === "system" || i.created_by_name === "Auto (request)" || (i.label || "").startsWith("Invite \u2014 ") || (i.label || "").startsWith("Invite: ");
                const automated = invites.filter(isAuto);
                const controlled = invites.filter((i) => !isAuto(i));
                const autoJoins = automated.reduce((s, i) => s + i.joins, 0);
                return (
                  <div className="space-y-2">
                    {controlled.map(renderRow)}
                    {automated.length > 0 && (
                      <div className="border border-white/10 bg-[#121212]" data-testid="automated-keys-group">
                        <button onClick={() => setShowAuto((v) => !v)} data-testid="automated-keys-toggle" className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-white/[0.02] transition-colors">
                          <div className="min-w-0">
                            <p className="font-body text-sm text-white">Automated request keys</p>
                            <p className="font-body text-xs text-zinc-500 mt-0.5">{automated.length} key{automated.length !== 1 ? "s" : ""} auto-forged from invite requests · {autoJoins} joined</p>
                          </div>
                          <ChevronDown size={18} className={`text-zinc-400 shrink-0 transition-transform ${showAuto ? "rotate-180" : ""}`} />
                        </button>
                        {showAuto && (
                          <div className="border-t border-white/10 p-2 space-y-2" data-testid="automated-keys-list">
                            {automated.map(renderRow)}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}
            </>
          )}
        </div>
      )}

      {tab === "watch_inbox" && watchSection === "flags" && (
        <div data-testid="watch-tab">
          <p className="font-body text-sm text-zinc-400 mb-4 leading-relaxed">Disturbances raised by members. Reporters are shown to the Council but stay anonymous to the reported member. Flagged posts &amp; comments are hidden immediately; dismiss to restore, remove to delete permanently, or remove the member from the network.</p>
          {safetyState && !safetyState.exists && watchSteward && (
            <div className="border border-[#14A89A]/30 bg-[#14A89A]/[0.06] p-4 mb-5" data-testid="safety-activate">
              <p className="font-body text-sm text-white mb-1">Activate the Safety Review key</p>
              <p className="font-body text-xs text-zinc-400 mb-3 leading-relaxed">Flagged content is encrypted so only Security &amp; Signal stewards can read it. As a steward, generate the key once to begin reviewing reported content. It will be shared automatically with other stewards.</p>
              <button onClick={activateSafetyKey} disabled={safetyBusy} data-testid="safety-activate-btn" className="bg-[#14A89A] text-[#0A0A0A] font-semibold px-4 py-2 font-body text-xs disabled:opacity-40">{safetyBusy ? "Activating…" : "Activate Safety Review key"}</button>
            </div>
          )}
          {safetyState && safetyState.exists && watchSteward && !safetyState.provisioned && (
            <div className="border border-amber-400/30 bg-amber-400/[0.06] p-4 mb-5" data-testid="safety-pending">
              <p className="font-body text-xs text-amber-200/90 leading-relaxed">The Safety Review key exists but hasn't been shared with your account yet. Ask the Founder (or an existing steward) to open The Watch to grant you access.</p>
            </div>
          )}
          {watchSteward && bgAudit.length > 0 && (
            <details className="border border-white/10 bg-[#0E0E0E] mb-5" data-testid="break-glass-audit">
              <summary className="px-4 py-3 cursor-pointer font-body text-sm text-zinc-300 flex items-center gap-2 select-none"><ShieldCheck size={14} className="text-[#A855F7]" /> Break-glass access log <span className="text-zinc-600 text-xs">({bgAudit.length})</span></summary>
              <div className="px-4 pb-3 space-y-2">
                <p className="font-body text-[11px] text-zinc-600 mb-1">A permanent record of every request to open flagged content, who asked, who co-signed, and when. This can never be edited or removed.</p>
                {bgAudit.map((a) => (
                  <div key={a.id} className="border-t border-white/5 pt-2 font-body text-xs" data-testid={`bg-audit-${a.id}`}>
                    <div className="text-zinc-300"><span className="text-white">{a.requester_name}</span> requested to open a flagged {a.target_type || "item"} <span className="text-zinc-600">· {a.created_at ? new Date(a.created_at).toLocaleString() : ""}</span></div>
                    {(a.cosigns || []).length > 0 ? a.cosigns.map((c, i) => (
                      <div key={i} className="text-[#A855F7]/90 pl-3">✓ co-signed by <span className="text-white">{c.name}</span> <span className="text-zinc-600">· {c.at ? new Date(c.at).toLocaleString() : ""}</span></div>
                    )) : <div className="text-amber-400/80 pl-3">awaiting a second steward's co-signature</div>}
                    {a.revealed_at && <div className="text-zinc-500 pl-3">opened {new Date(a.revealed_at).toLocaleString()}</div>}
                  </div>
                ))}
              </div>
            </details>
          )}
          {watchSteward && cosignQueue.length > 0 && (
            <div className="border border-[#A855F7]/40 bg-[#A855F7]/[0.06] p-4 mb-5" data-testid="cosign-queue">
              <p className="font-body text-sm text-white mb-1 flex items-center gap-2"><ShieldCheck size={15} className="text-[#A855F7]" /> Co-signatures needed</p>
              <p className="font-body text-xs text-zinc-400 mb-3 leading-relaxed">Another steward is asking to open a flagged item. Opening requires two of you, co-sign only if you agree there's a genuine safety reason to look.</p>
              <div className="space-y-2">
                {cosignQueue.map((r) => (
                  <div key={r.id} className="flex items-center justify-between gap-3 border-t border-white/5 pt-2" data-testid={`cosign-${r.id}`}>
                    <div className="min-w-0">
                      <span className="font-body text-xs text-zinc-200">{r.requester_name}</span>
                      <span className="font-body text-[11px] text-zinc-500"> wants to open a flagged {r.target_type || "item"}</span>
                    </div>
                    <button onClick={() => approveBreakGlass(r)} data-testid={`cosign-approve-${r.id}`} className="shrink-0 inline-flex items-center gap-1.5 bg-[#A855F7]/90 text-white px-3 py-1.5 font-body text-xs hover:bg-[#A855F7]"><ShieldCheck size={13} /> Co-sign</button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {watch === null ? <SignalPulse size={40} /> :
            watch.length === 0 ? <p className="font-body text-sm text-zinc-500">All quiet. No disturbances in the field.</p> :
            <div className="space-y-4">
              {watch.map((d) => (
                <div key={d.id} className="bg-[#121212] border border-white/10 p-5" data-testid="disturbance-card">
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <div className="flex items-center gap-2">
                      <Flag size={15} className="text-[#A855F7]" />
                      <span className="font-body text-[10px] uppercase tracking-wider text-zinc-400">{d.target_type}</span>
                      <span className={`font-body text-[10px] uppercase tracking-wider px-2 py-0.5 border ${d.status === "open" ? "border-[#A855F7]/50 text-[#A855F7]" : "border-white/10 text-zinc-500"}`}>{d.status}</span>
                    </div>
                    <span className="font-body text-[10px] text-zinc-600">by {d.reporter_name}</span>
                  </div>
                  {d.target_author_name && <p className="font-body text-xs text-zinc-500 mb-2">Reported member: <span className="text-zinc-300">{d.target_author_name}</span></p>}
                  {d.has_content && (
                    <div className="border border-white/10 bg-black/30 p-3 mb-2" data-testid="disturbance-content">
                      <p className="font-body text-[10px] uppercase tracking-wider text-zinc-600 mb-1">Flagged content</p>
                      {d._content ? (
                        <p className="font-body text-xs text-zinc-300 whitespace-pre-wrap break-words">{d._content}</p>
                      ) : (d.sr?.v === 2 && watchSteward) ? (
                        d._bg ? (
                          <p className="font-body text-xs text-amber-300/90 italic flex items-center gap-1.5" data-testid={`bg-waiting-${d.id}`}><ShieldCheck size={12} /> Waiting for a second steward to co-sign… ({d._bg.cosign_count}/1)</p>
                        ) : (
                          <div>
                            <p className="font-body text-xs text-zinc-500 italic mb-2">🔒 Sealed. Opening a flagged item requires two stewards, no one can read it alone.</p>
                            <button onClick={() => startBreakGlass(d.id)} data-testid={`bg-request-${d.id}`} className="inline-flex items-center gap-1.5 border border-[#A855F7]/50 text-[#A855F7] px-3 py-1.5 font-body text-xs hover:bg-[#A855F7]/10"><ShieldCheck size={13} /> Request to open (needs a 2nd steward)</button>
                          </div>
                        )
                      ) : watchSteward ? (
                        <p className="font-body text-xs text-zinc-500 italic">Encrypted: unlock the Safety Review key to view.</p>
                      ) : (
                        <p className="font-body text-xs text-zinc-500 italic">🔒 Encrypted: only Security &amp; Signal stewards can view flagged content.</p>
                      )}
                    </div>
                  )}
                  {d.reason && <p className="font-body text-xs text-zinc-400 mb-3"><span className="text-zinc-600">Reason: </span>{d.reason}</p>}
                  {(d.reporter_id || d.target_author_id) && (
                    <div className="flex flex-wrap gap-3 mt-2" data-testid={`disturbance-reply-row-${d.id}`}>
                      {d.reporter_id && <a href={`/messages/${d.reporter_id}`} data-testid={`disturbance-reply-reporter-${d.id}`} className="inline-flex items-center gap-1.5 border border-[#14A89A]/60 text-[#14A89A] hover:bg-[#14A89A] hover:text-[#0A0A0A] px-3 py-1.5 font-body text-xs transition-colors"><MessageSquare size={13} /> Reply to {d.reporter_name && d.reporter_name !== "Unknown" ? d.reporter_name : "reporter"}</a>}
                      {d.target_author_id && <a href={`/messages/${d.target_author_id}`} data-testid={`disturbance-message-author-${d.id}`} className="inline-flex items-center gap-1.5 border border-white/15 text-zinc-300 hover:bg-white/5 hover:text-white px-3 py-1.5 font-body text-xs transition-colors"><MessageSquare size={13} /> Message {d.target_author_name || "reported member"}</a>}
                    </div>
                  )}
                  {d.status === "open" && (
                    <div className="flex flex-wrap gap-3 mt-3">
                      <button onClick={() => resolveDisturbance(d.id, "dismiss")} data-testid={`dismiss-${d.id}`} className="flex items-center gap-2 border border-white/15 text-zinc-300 px-4 py-2 font-body text-xs hover:bg-white/5 transition-colors"><Check size={14} /> Dismiss &amp; restore</button>
                      {(d.target_type === "post" || d.target_type === "comment") && (
                        <button onClick={() => resolveDisturbance(d.id, "remove")} data-testid={`remove-${d.id}`} className="flex items-center gap-2 border border-white/15 text-zinc-300 px-4 py-2 font-body text-xs hover:bg-red-600/20 hover:border-red-600/40 transition-colors"><Trash2 size={14} /> Delete content</button>
                      )}
                      {d.target_author_id && (
                        <button onClick={() => { if (window.confirm("Remove this member from the network? They will lose access immediately.")) resolveDisturbance(d.id, "ban"); }} data-testid={`ban-${d.id}`} className="flex items-center gap-2 bg-red-600/90 text-white px-4 py-2 font-body text-xs hover:bg-red-600 transition-colors"><Ban size={14} /> Remove member</button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>}
        </div>
      )}

      {tab === "support" && (
        <div data-testid="support-tab" className="space-y-8">
          <div className="bg-[#121212] border border-white/10 p-6">
            <div className="flex items-center gap-2 mb-1">
              <HeartHandshake size={18} className="text-[#14A89A]" />
              <h2 className="font-heading text-lg text-white">Donations received</h2>
            </div>
            <p className="font-body text-sm text-zinc-400 mb-5">Card donations via Stripe (test mode until your live keys are connected).</p>
            {donations === null ? <SignalPulse size={40} /> : (
              <>
                <div className="flex gap-8 mb-5">
                  <div><div className="font-heading text-2xl text-[#14A89A]" data-testid="donations-total">${donations.total.toFixed(2)}</div><div className="font-body text-[10px] uppercase tracking-wider text-zinc-500">Total raised</div></div>
                  <div><div className="font-heading text-2xl text-white">{donations.count}</div><div className="font-body text-[10px] uppercase tracking-wider text-zinc-500">Donations</div></div>
                </div>
                {donations.donations.length > 0 && (
                  <div className="space-y-2">
                    {donations.donations.slice(0, 12).map((d, i) => (
                      <div key={i} className="flex items-center justify-between border-b border-white/5 pb-2">
                        <span className="font-body text-sm text-zinc-300">{d.user_email || "Anonymous"}</span>
                        <span className="font-heading text-sm text-[#14A89A]">${Number(d.amount).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="bg-[#121212] border border-white/10 p-6">
            <div className="flex items-center gap-2 mb-1">
              <Bitcoin size={18} className="text-[#A855F7]" />
              <h2 className="font-heading text-lg text-white">Crypto donation addresses</h2>
            </div>
            <p className="font-body text-sm text-zinc-400 mb-5">Paste your own wallet addresses. These are shown to members on the Donate page with a QR code. Leave blank to hide that coin. Self-custodied, no processor ever touches these funds.</p>
            {cryptoCfg === null ? <SignalPulse size={40} /> : (
              <div className="space-y-4">
                {[["btc", "Bitcoin (BTC)"], ["eth", "Ethereum (ETH)"], ["atom", "Cosmos (ATOM)"]].map(([k, label]) => (
                  <div key={k}>
                    <label className="font-body text-xs text-zinc-400 mb-1.5 block">{label}</label>
                    <input value={cryptoCfg[k] || ""} onChange={(e) => setCryptoCfg({ ...cryptoCfg, [k]: e.target.value })} data-testid={`crypto-input-${k}`} placeholder={`Your ${label} address`} className="w-full bg-[#0A0A0A] border border-white/15 px-4 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#14A89A] font-mono" />
                  </div>
                ))}
                <button onClick={saveCrypto} disabled={savingCrypto} data-testid="save-crypto-button" className="flex items-center gap-2 bg-[#14A89A] text-[#0A0A0A] px-5 py-2.5 font-body text-sm font-medium hover:bg-[#2BC4B5] transition-colors disabled:opacity-40">
                  {savingCrypto ? <Loader2 className="animate-spin" size={16} /> : cryptoSaved ? <><Check size={16} /> Saved</> : "Save addresses"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "quiet" && (
        <div data-testid="quiet-tab">
          <p className="font-body text-sm text-zinc-400 mb-6 leading-relaxed">Members who haven't stepped inside for 4+ months, and anyone you've archived. Send a gentle re-entry email first, many quietly return. Archived members are welcomed straight back the moment they log in again.</p>
          {dormant === null ? <SignalPulse size={40} /> :
            dormant.length === 0 ? <p className="font-body text-sm text-zinc-500">No quiet members. Everyone's been active within the last 4 months.</p> :
            <>
              <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                <span className="font-body text-xs tracking-[0.2em] uppercase text-zinc-500">{dormant.filter((d) => d.status !== "archived").length} quiet · {dormant.filter((d) => d.status === "archived").length} archived</span>
                <button onClick={reentryAll} disabled={dormantBusy === "all"} data-testid="reentry-all-button" className="flex items-center gap-2 border border-[#14A89A]/40 text-[#14A89A] px-4 py-2 font-body text-xs hover:bg-[#14A89A]/10 transition-colors disabled:opacity-40">
                  {dormantBusy === "all" ? <Loader2 className="animate-spin" size={14} /> : <Mail size={14} />} Email all quiet members
                </button>
              </div>
              {nudgeStatus && nudgeStatus.last_run_at && (
                <p className="font-body text-[11px] text-zinc-600 mb-5 -mt-2" data-testid="nudge-status">
                  Auto-nudge last ran {new Date(nudgeStatus.last_run_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })} · sent {nudgeStatus.last_sent || 0} email{nudgeStatus.last_sent === 1 ? "" : "s"}. Runs daily; each quiet member is nudged at 4 months, then again 3 and 6 months later, max 3.
                </p>
              )}
              <div className="flex gap-2 mb-5 flex-wrap" data-testid="quiet-filters">
                {[["all", "All"], ["long_gone", "Long gone"], ["archived", "Archived"]].map(([k, l]) => {
                  const n = k === "all" ? dormant.length : k === "archived" ? dormant.filter((d) => d.status === "archived").length : dormant.filter((d) => d.status !== "archived" && (d.reentry_count || 0) >= 3).length;
                  return (
                    <button key={k} onClick={() => setQuietFilter(k)} data-testid={`quiet-filter-${k}`} className={`px-3 py-1.5 font-body text-xs border transition-colors ${quietFilter === k ? "border-[#14A89A] text-[#14A89A]" : "border-white/15 text-zinc-400 hover:text-white"}`}>{l}{n > 0 ? ` · ${n}` : ""}</button>
                  );
                })}
              </div>
              {quietFilter === "long_gone" && dormant.filter((d) => d.status !== "archived" && (d.reentry_count || 0) >= 3).length > 0 && (
                <div className="mb-5 border border-white/10 bg-[#121212] p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="long-gone-bulk-bar">
                  <p className="font-body text-xs text-zinc-400 max-w-md">These members were nudged the full 3 times and never returned. Archive them all in one pass, they're auto-restored the moment they ever log back in.</p>
                  <div className="flex gap-2 flex-wrap">
                    <button onClick={() => archiveLongGone("keep_directory")} disabled={dormantBusy === "longgone"} data-testid="archive-long-gone-keep" className="flex items-center gap-2 border border-white/15 text-zinc-300 px-4 py-2 font-body text-xs hover:bg-white/5 transition-colors disabled:opacity-40"><Archive size={14} /> Archive all · keep directory</button>
                    <button onClick={() => archiveLongGone("full")} disabled={dormantBusy === "longgone"} data-testid="archive-long-gone-full" className="flex items-center gap-2 border border-white/15 text-zinc-300 px-4 py-2 font-body text-xs hover:bg-red-600/20 hover:border-red-600/40 transition-colors disabled:opacity-40">{dormantBusy === "longgone" ? <Loader2 className="animate-spin" size={14} /> : <Archive size={14} />} Archive all</button>
                  </div>
                </div>
              )}
              <div className="space-y-4">
                {dormant.filter((d) => quietFilter === "all" ? true : quietFilter === "archived" ? d.status === "archived" : (d.status !== "archived" && (d.reentry_count || 0) >= 3)).map((d) => {
                  const months = d.days_dormant != null ? Math.floor(d.days_dormant / 30) : null;
                  const archived = d.status === "archived";
                  const busy = dormantBusy === d.id;
                  return (
                    <div key={d.id} data-testid={`dormant-card-${d.id}`} className={`bg-[#121212] border p-5 ${archived ? "border-white/8 opacity-80" : "border-white/10"}`}>
                      <div className="flex items-start gap-4">
                        <Avatar src={d.avatar} alt={d.name} size={44} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span data-testid={`dormant-name-${d.id}`} className="font-body text-sm text-white font-medium">{d.name}</span>
                            {archived && <span className="font-body text-[10px] tracking-[0.15em] uppercase border border-white/15 text-zinc-400 px-2 py-0.5">{d.archive_mode === "keep_directory" ? "Archived · directory kept" : "Archived"}</span>}
                            {!archived && (d.reentry_count || 0) >= 3 && <span data-testid={`long-gone-tag-${d.id}`} className="font-body text-[10px] tracking-[0.15em] uppercase border border-[#D4AF37]/40 text-[#D4AF37] px-2 py-0.5">Long gone</span>}
                          </div>
                          {d.headline && <p className="font-body text-xs text-zinc-500 mt-0.5 truncate">{d.headline}</p>}
                          <p className="font-body text-xs text-zinc-500 mt-1.5 flex items-center gap-1.5">
                            <Clock size={12} /> Quiet for {months != null ? `~${months} month${months === 1 ? "" : "s"}` : "a long while"}
                            {d.reentry_count > 0 && <span className="text-[#14A89A]/70"> · nudged {d.reentry_count}/3{d.reentry_count >= 3 ? " (max reached)" : ""}</span>}
                            {d.email_opt_out && <span className="text-zinc-600"> · unsubscribed</span>}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap mt-4 pl-[60px]">
                        {archived ? (
                          <button onClick={() => restoreDormant(d.id)} disabled={busy} data-testid={`restore-${d.id}`} className="flex items-center gap-2 border border-white/15 text-zinc-300 px-4 py-2 font-body text-xs hover:bg-white/5 transition-colors disabled:opacity-40">
                            {busy ? <Loader2 className="animate-spin" size={14} /> : <Undo2 size={14} />} Restore member
                          </button>
                        ) : (
                          <>
                            <button onClick={() => sendReentry(d.id)} disabled={busy || d.email_opt_out} data-testid={`reentry-${d.id}`} className="flex items-center gap-2 border border-[#14A89A]/40 text-[#14A89A] px-4 py-2 font-body text-xs hover:bg-[#14A89A]/10 transition-colors disabled:opacity-40" title={d.email_opt_out ? "This member has unsubscribed" : ""}>
                              {busy ? <Loader2 className="animate-spin" size={14} /> : <Mail size={14} />} {d.reentry_email_at ? "Re-send re-entry email" : "Send re-entry email"}
                            </button>
                            <button onClick={() => archiveDormant(d.id, "keep_directory")} disabled={busy} data-testid={`archive-keep-${d.id}`} className="flex items-center gap-2 border border-white/15 text-zinc-300 px-4 py-2 font-body text-xs hover:bg-white/5 transition-colors disabled:opacity-40">
                              <Archive size={14} /> Archive · keep directory
                            </button>
                            <button onClick={() => archiveDormant(d.id, "full")} disabled={busy} data-testid={`archive-full-${d.id}`} className="flex items-center gap-2 border border-white/15 text-zinc-300 px-4 py-2 font-body text-xs hover:bg-red-600/20 hover:border-red-600/40 transition-colors disabled:opacity-40">
                              <Archive size={14} /> Archive account
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>}
        </div>
      )}

      {tab === "beacon" && (
        <div data-testid="beacon-admin">
          <BeaconLaunchWindow />
          <BeaconMilestonesPanel />
          <p className="font-body text-sm text-zinc-400 mb-6 leading-relaxed">Beacon Profile requests and live creator spaces. Approve to unlock a member's teaching space (toggle whether their hosting is comped/free), or decline. Everyone is comped for launch.</p>
          {beacons === null ? <SignalPulse size={40} /> :
            beacons.length === 0 ? <p className="font-body text-sm text-zinc-500">No Beacon requests yet.</p> :
            <div className="space-y-3">
              {beacons.map((b) => (
                <div key={b.id} className="border border-white/10 bg-[#121212] p-4" data-testid={`beacon-admin-${b.id}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-3">
                      <Avatar name={b.owner_name} src={b.owner_avatar} iv={b.owner_avatar_iv} size={40} />
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-body text-white font-medium" data-testid={`beacon-requester-${b.id}`}>{b.owner_name || b.title || b.creator_name || "A sovereign"}</span>
                          <span className="font-body text-[10px] tracking-[0.15em] uppercase px-2 py-0.5 border border-white/15 text-zinc-400">{b.status}</span>
                          {b.status !== "pending" && <span className="font-body text-[11px] text-zinc-500">{b.infra_status === "comped" ? "comped" : "paid infra"} · {b.price_monthly > 0 ? `$${b.price_monthly}/mo` : "free"}</span>}
                        </div>
                        {b.requested_at && <div className="font-body text-[11px] text-zinc-500 mt-0.5" data-testid={`beacon-requested-at-${b.id}`}>Requested {new Date(b.requested_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}</div>}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {b.status === "pending" ? (
                        <>
                          <button onClick={() => approveBeacon(b.id, true)} data-testid={`beacon-approve-comped-${b.id}`} className="border border-[#14A89A]/40 text-[#14A89A] px-3 py-1.5 font-body text-xs hover:bg-[#14A89A]/10">Approve · comped</button>
                          <button onClick={() => approveBeacon(b.id, false)} data-testid={`beacon-approve-paid-${b.id}`} className="border border-white/15 text-zinc-300 px-3 py-1.5 font-body text-xs hover:bg-white/5">Approve · paid</button>
                          <button onClick={() => declineBeacon(b.id)} data-testid={`beacon-decline-${b.id}`} className="border border-white/15 text-zinc-400 px-3 py-1.5 font-body text-xs hover:bg-red-600/20">Decline</button>
                        </>
                      ) : b.handle ? <a href={`/beacon/view/${b.handle}`} className="text-[#14A89A] font-body text-xs hover:underline">view</a> : null}
                    </div>
                  </div>
                  {b.project_desc && <p className="font-body text-sm text-zinc-400 mt-2">{b.project_desc}</p>}
                </div>
              ))}
            </div>}
        </div>
      )}

      {tab === "tips" && <TipsManager />}

      {tab === "stream" && <StreamManager />}

      {tab === "backup" && (
        <div data-testid="backup-tab">
          <div className="bg-[#121212] border border-white/10 p-6 space-y-5">
            <div>
              <h2 className="font-heading text-lg text-white">Full network backup</h2>
              <p className="font-body text-sm text-zinc-400 mt-1 leading-relaxed">Download a complete, portable copy of the network - all accounts, posts, messages, keys and media (stored encrypted, exactly as they live on the server). If this site is ever taken down, this file plus the source code lets you restore everything on a new, sovereign server and continue.</p>
            </div>
            {backupStats && (
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-3" data-testid="backup-stats">
                {Object.entries(backupStats).map(([k, v]) => (
                  <div key={k} className="border border-white/10 px-3 py-2">
                    <div className="font-heading text-xl text-[#14A89A]">{v}</div>
                    <div className="font-body text-[10px] uppercase tracking-wider text-zinc-500">{k.replace("_", " ")}</div>
                  </div>
                ))}
              </div>
            )}
            <button onClick={downloadBackup} disabled={downloading} data-testid="download-backup-button" className="flex items-center gap-2 bg-[#14A89A] text-[#0A0A0A] px-5 py-2.5 font-body text-sm font-medium hover:bg-[#2BC4B5] transition-colors disabled:opacity-40">
              {downloading ? <Loader2 className="animate-spin" size={16} /> : <Download size={16} />} Download full backup
            </button>
            <div className="border-t border-white/10 pt-4 space-y-1.5">
              <p className="font-body text-xs text-zinc-500">• Do this regularly (weekly is wise). Store copies in two places - e.g. your computer and an offline drive.</p>
              <p className="font-body text-xs text-zinc-500">• Keep your source code saved to GitHub. Code + this backup = full sovereignty.</p>
              <p className="font-body text-xs text-zinc-500">• The file is large if there's lots of media - let the download finish.</p>
            </div>
          </div>
        </div>
      )}

      {tab === "brand" && can("refresh") && (
        <div data-testid="brand-tab" className="space-y-6">
          <div className="bg-[#121212] border border-white/10 p-6 space-y-5">
            <div>
              <h2 className="font-heading text-lg text-white">Refresh social preview</h2>
              <p className="font-body text-sm text-zinc-400 mt-1 leading-relaxed">After a big change (a new logo, banner or tagline), chat apps and social platforms keep showing the <span className="text-white">old</span> link preview because they cache it. Use these to force each platform to re-fetch the new Tree-of-Life thumbnail. Paste the site URL if it isn't pre-filled, then hit "Scrape / Refresh".</p>
            </div>
            <div className="flex items-center gap-2 border border-white/10 bg-black/40 px-3 py-2 max-w-md">
              <span className="font-body text-sm text-zinc-300 flex-1 truncate">{SITE}</span>
              <button onClick={copySite} data-testid="brand-copy-url" className="flex items-center gap-1.5 text-xs font-body text-[#14A89A] hover:text-white transition-colors shrink-0"><Copy size={13} /> {copied ? "Copied" : "Copy"}</button>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              {[
                { label: "Facebook · iMessage · WhatsApp", href: `https://developers.facebook.com/tools/debug/?q=${encodeURIComponent(SITE)}`, testid: "brand-refresh-facebook" },
                { label: "LinkedIn", href: `https://www.linkedin.com/post-inspector/inspect/${encodeURIComponent(SITE)}`, testid: "brand-refresh-linkedin" },
                { label: "X (Twitter)", href: "https://cards-dev.twitter.com/validator", testid: "brand-refresh-twitter" },
              ].map((l) => (
                <a key={l.testid} href={l.href} target="_blank" rel="noreferrer" data-testid={l.testid} className="flex items-center justify-between gap-2 border border-white/10 hover:border-[#14A89A]/60 bg-black/30 px-4 py-3 transition-colors group">
                  <span className="font-body text-sm text-zinc-200 group-hover:text-white">{l.label}</span>
                  <ExternalLink size={14} className="text-zinc-500 group-hover:text-[#14A89A] transition-colors" />
                </a>
              ))}
            </div>
            <div className="border-t border-white/10 pt-4 space-y-1.5">
              <p className="font-body text-xs text-zinc-500">• On Facebook's debugger, click <span className="text-zinc-300">"Scrape Again"</span>: this also refreshes iMessage &amp; WhatsApp previews.</p>
              <p className="font-body text-xs text-zinc-500">• Browser tab still showing the old icon? Hard refresh (Cmd/Ctrl + Shift + R), the favicon is cache-busted and will update.</p>
              <p className="font-body text-xs text-zinc-500">• New shares always use the latest thumbnail automatically, this is only for links shared <span className="text-zinc-300">before</span> the change.</p>
            </div>
          </div>
        </div>
      )}

      {tab === "council" && isFounder && (
        <div className="space-y-6">
          <div className="flex items-center justify-between gap-4 border border-white/10 bg-[#121212] px-5 py-4" data-testid="profile-referral-toggle-row">
            <div className="min-w-0">
              <p className="font-body text-sm text-white">Show referral count on member profiles</p>
              <p className="font-body text-xs text-zinc-500 mt-0.5">When on, each member sees a private "You've brought in N sovereigns" badge on their own profile. Off for now, turn on when you're ready.</p>
            </div>
            <button onClick={toggleProfileReferralCount} data-testid="profile-referral-toggle" role="switch" aria-checked={!!profileReferralCount} className={`relative shrink-0 w-12 h-6 rounded-full transition-colors ${profileReferralCount ? "bg-[#14A89A]" : "bg-zinc-700"}`}>
              <span className={`absolute top-0.5 h-5 w-5 bg-white rounded-full transition-all ${profileReferralCount ? "left-[26px]" : "left-0.5"}`} />
            </button>
          </div>
          <CouncilManager />
        </div>
      )}
    </div>
  );
}
